import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, useTheme, Grid, FormControl, Select, MenuItem,
  IconButton, Chip, CircularProgress, Tooltip, Button, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  PlaylistAddCheck as RunAllIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FitnessCenter as GymIcon,
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  OpenInNew as OpenIcon,
  HelpOutline as NoDataIcon,
  History as HistoryIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectGradeIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Notifications as NotificationsIcon,
  Analytics as AnalyticsIcon,
  CheckCircleOutline as CorrectIcon,
  CancelOutlined as WrongIcon,
  RemoveCircleOutline as PartialIcon,
  Lightbulb as HintIcon,
  GppGood as GroundedIcon,
  GppMaybe as UngroundedIcon,
} from '@mui/icons-material';
import {
  RootState,
  GoldenSetEntry,
  GoldenSetReport,
  GoldenSetCandidate,
  GoldenSetRunResult,
  CuratorNotification,
  RecallGap,
  LowRecallTicket,
  MissedChunk,
  PipelineStage,
  PipelineTrace,
  IncidentMemory,
} from '../types';
import { CuratorService } from '../services/curatorService';
import { fetchClients } from '../store/slices/clientsSlice';
import { PlaybooksService } from '../services/playbooksService';
import { getRelativeTimeString } from '../utils/dateUtils';
import { DataTable, Column } from '../components/common';
import { colorUtils } from '../theme';

// ── Curate dialog ─────────────────────────────────────────────────────────────

interface CurateDialogProps {
  candidate: GoldenSetCandidate | null;
  clientId: string;
  onClose: () => void;
  onCurated: () => void;
}

const CurateDialog: React.FC<CurateDialogProps> = ({ candidate, clientId, onClose, onCurated }) => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (candidate) {
      setName(candidate.incident_type ? `${candidate.incident_type} — ${(candidate.created_at || '').slice(0, 10)}` : `IOC session — ${(candidate.created_at || '').slice(0, 10)}`);
      setNotes('');
      setError(null);
    }
  }, [candidate]);

  const handleSubmit = async () => {
    if (!candidate) return;
    setLoading(true);
    setError(null);
    try {
      await PlaybooksService.createGoldenSetEntry({ client_id: clientId, session_id: candidate.session_id, name: name.trim() || undefined, notes: notes.trim() || undefined });
      onCurated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!candidate} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Add to Golden Set</Typography>
        {candidate && (
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.25 }}>
            Session {candidate.session_id.slice(0, 8)}… · quality score {(candidate.quality_score * 100).toFixed(0)}%
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {candidate?.quality_breakdown && (
          <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
              Why this candidate — {(candidate.quality_score * 100).toFixed(0)}% quality score
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[
                { label: 'Analyst acceptance rate', value: candidate.quality_breakdown.acceptance, max: 0.40 },
                { label: 'Step completion', value: candidate.quality_breakdown.steps, max: 0.35 },
                { label: 'Root cause documented', value: candidate.quality_breakdown.root_cause, max: 0.15 },
                { label: 'MITRE tags present', value: candidate.quality_breakdown.mitre, max: 0.10 },
              ].map(({ label, value, max }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.74rem', color: theme.palette.text.secondary, minWidth: 170, flexShrink: 0 }}>{label}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(value / max) * 100}
                    sx={{
                      flex: 1, height: 6, borderRadius: 3, backgroundColor: theme.palette.background.default,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: value >= max ? theme.palette.success.main : value > 0 ? theme.palette.warning.main : theme.palette.text.disabled,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: theme.palette.text.tertiary, minWidth: 64, textAlign: 'right', flexShrink: 0 }}>
                    {Math.round(value * 100)}/{Math.round(max * 100)} pts
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {candidate && (
          <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {candidate.ai_summary_preview && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.25 }}>AI summary</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, lineHeight: 1.4 }}>{candidate.ai_summary_preview}</Typography>
              </Box>
            )}
            {candidate.root_cause && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.25 }}>Root cause</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, lineHeight: 1.4 }}>{candidate.root_cause}</Typography>
              </Box>
            )}
            {candidate.mitre_tags?.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.375 }}>MITRE techniques</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {candidate.mitre_tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, fontFamily: 'monospace', backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />
                  ))}
                </Box>
              </Box>
            )}
            {!candidate.ai_summary_preview && !candidate.root_cause && !candidate.mitre_tags?.length && (
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>No summary, root cause, or MITRE data recorded for this session.</Typography>
            )}
          </Box>
        )}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }}
        />
        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          size="small"
          fullWidth
          placeholder="Why is this session exemplary?"
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.main } } }}
        />
        {error && <Typography variant="caption" sx={{ color: theme.palette.error.main }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
        <Button variant="contained" size="small" disabled={loading} onClick={handleSubmit} startIcon={loading ? <CircularProgress size={13} color="inherit" /> : <AddIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 600, px: 2 }}>
          {loading ? 'Adding…' : 'Add to golden set'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Run results dialog ────────────────────────────────────────────────────────

// ── Shared utilities ──────────────────────────────────────────────────────────

// Word-level diff — returns array of {text, type: 'same'|'add'|'del'}
function diffWords(a: string, b: string): { text: string; type: 'same' | 'add' | 'del' }[] {
  const wordsA = a.split(/(\s+)/);
  const wordsB = b.split(/(\s+)/);
  const m = wordsA.length, n = wordsB.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i][j] = wordsA[i] === wordsB[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const result: { text: string; type: 'same' | 'add' | 'del' }[] = [];
  let i = 0, j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && wordsA[i] === wordsB[j]) {
      result.push({ text: wordsA[i], type: 'same' }); i++; j++;
    } else if (j < n && (i >= m || dp[i][j + 1] >= dp[i + 1][j])) {
      result.push({ text: wordsB[j], type: 'add' }); j++;
    } else {
      result.push({ text: wordsA[i], type: 'del' }); i++;
    }
  }
  return result;
}

// Friendlier labels for stage detail fields — the raw key names (count, model,
// c_star_count...) read as meaningless out of context, especially a bare number
// like "count: 4" with nothing saying what was counted.
const STAGE_DETAIL_LABELS: Record<string, Record<string, string>> = {
  rewriter: { semantic_query: 'Semantic search query', keyword_query: 'Keyword search query' },
  golden_lookup: { status: 'Match status', similarity: 'Similarity to matched entry', c_star_count: 'Expected past incidents', matched_entry: 'Matched golden entry' },
  vector_search: { count: 'Candidates found (vector search)', top_matches: 'Top vector matches' },
  bm25_search: { count: 'Candidates found (keyword search)', keyword_query: 'Keyword search query', top_matches: 'Top keyword matches' },
  rrf: { candidate_count: 'Candidates after merging vector + keyword' },
  reranker: { model: 'Reranker model', count: 'Results after reranking', top_matches: 'Top reranked matches' },
  recall: { recall: 'Coverage', precision_score: 'Relevance', c_star_count: 'Expected past incidents', expected_incidents: 'Expected past incidents', missed_incidents: 'Missed past incidents', found_incidents: 'Found past incidents' },
};

const detailLabel = (stageName: string, key: string): string =>
  STAGE_DETAIL_LABELS[stageName]?.[key] ?? key.replace(/_/g, ' ');

// Stage accordion row — auto-expands on warning
const StageRow: React.FC<{
  stage: PipelineStage;
  isWarn: boolean;
  hasDetails: boolean;
  color: string;
  fmtStage: (s: string) => string;
}> = ({ stage, isWarn, hasDetails, color, fmtStage }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(isWarn);
  return (
    <Box sx={{ py: 0.75, borderBottom: `1px solid ${theme.palette.border.light}` }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: hasDetails ? 'pointer' : 'default' }}
        onClick={() => hasDetails && setExpanded(p => !p)}
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color, minWidth: 14, lineHeight: 1 }}>
          {isWarn ? '⚠' : stage.status === 'skipped' ? '—' : '✓'}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1 }}>{fmtStage(stage.name)}</Typography>
        {stage.duration_ms != null && (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: 'monospace', fontSize: '0.65rem' }}>
            {stage.duration_ms < 1000 ? `${stage.duration_ms}ms` : `${(stage.duration_ms / 1000).toFixed(1)}s`}
          </Typography>
        )}
        {hasDetails && (
          <Typography sx={{ fontSize: '0.65rem', color: theme.palette.text.disabled, lineHeight: 1 }}>{expanded ? '▲' : '▼'}</Typography>
        )}
      </Box>
      {hasDetails && expanded && (
        <Box sx={{ ml: 3.5, mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {Object.entries(stage.details).map(([k, v]) => (
            <Box key={k}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.6rem', display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{detailLabel(stage.name, k)}</Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}>{String(v)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Pipeline trace modal ──────────────────────────────────────────────────────

interface PipelineTraceModalProps {
  open: boolean;
  ticketId?: string | null;
  entryId?: string | null;
  runId?: string | null;
  onClose: () => void;
}

const PipelineTraceModal: React.FC<PipelineTraceModalProps> = ({ open, ticketId, entryId, runId, onClose }) => {
  const theme = useTheme();
  const [trace, setTrace] = useState<PipelineTrace | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setTrace(null); return; }
    setLoading(true);
    const req = ticketId
      ? CuratorService.getTicketTrace(ticketId)
      : entryId && runId ? CuratorService.getRunTrace(entryId, runId) : null;
    if (!req) { setLoading(false); return; }
    req.then(setTrace).catch(() => setTrace(null)).finally(() => setLoading(false));
  }, [open, ticketId, entryId, runId]);

  const fmtStage = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Pipeline Trace</Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.25 }}>
          {ticketId ? `Ticket ${ticketId.slice(0, 8)}…` : runId ? `Run ${runId.slice(0, 8)}…` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        ) : !trace ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.tertiary, py: 3, textAlign: 'center' }}>No trace available</Typography>
        ) : (
          <Box>
            {/* Summary metrics */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Coverage', v: trace.recall, fmt: (x: number) => `${Math.round(x * 100)}%`, warn: (x: number) => x < 0.75 },
                { label: 'Relevance', v: trace.precision, fmt: (x: number) => `${Math.round(x * 100)}%`, warn: () => false },
                { label: 'Golden match', v: trace.golden_similarity, fmt: (x: number) => `${Math.round(x * 100)}%`, warn: (x: number) => x < 0.8 },
                { label: 'Retrieved', v: trace.retrieved_count, fmt: (x: number) => `${x} cases`, warn: () => false },
              ].map(({ label, v, fmt, warn }) => (
                <Box key={label}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', display: 'block' }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.88rem', color: v != null && warn(v as number) ? theme.palette.warning.main : theme.palette.text.primary }}>
                    {v != null ? fmt(v as number) : '—'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Stage list — accordion, warning stages auto-expanded */}
            {trace.stages.map((stage, idx) => {
              const isWarn = stage.status === 'warning';
              const hasDetails = Object.keys(stage.details || {}).length > 0;
              const color = isWarn ? theme.palette.warning.main : stage.status === 'skipped' ? theme.palette.text.disabled : theme.palette.success.main;
              return (
                <StageRow key={idx} stage={stage} isWarn={isWarn} hasDetails={hasDetails} color={color} fmtStage={fmtStage} />
              );
            })}

            {/* Missed past cases */}
            {trace.missed_chunks?.length > 0 && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${theme.palette.border.light}` }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>
                  Missed past cases
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {trace.missed_chunks.map(chunk => (
                    <Box key={chunk.id} sx={{ p: 1, borderRadius: 1, backgroundColor: theme.palette.background.paper, border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.3)}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.375 }}>
                        {chunk.incident_type && (
                          <Chip label={chunk.incident_type.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, color: theme.palette.warning.main, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.12) }} />
                        )}
                        {chunk.date && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}>{getRelativeTimeString(chunk.date)}</Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', lineHeight: 1.4 }}>
                        {chunk.content_preview || 'Past incident no longer available'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Notifications tab ─────────────────────────────────────────────────────────

// Tab indices within the AI Gym page: 0 Inbox, 1 Coverage, 2 Golden Set, 3 Candidates.
// The notification's fix path always lives on one specific tab — this maps
// notification_type to where the curator actually needs to go to act on it.
const NOTIFICATION_ACTION_TAB: Record<string, { tab: number; label: string }> = {
  coverage_gap: { tab: 3, label: 'Review candidates →' },
  new_type_no_coverage: { tab: 3, label: 'Review candidates →' },
  systemic_miss: { tab: 1, label: 'Review in Coverage →' },
  benchmark_regression: { tab: 2, label: 'View Golden Set →' },
};

const NotificationsTab: React.FC<{ clientId: string; onViewTrace: (ticketId: string) => void; onGoToTab: (tab: number) => void }> = ({ clientId, onViewTrace, onGoToTab }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<CuratorNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CuratorService.getNotifications(clientId);
      setNotifications(res.notifications ?? []);
    } catch { setNotifications([]); } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [load, clientId]);

  const handleDismiss = async (id: string) => {
    setDismissingId(id);
    try {
      await CuratorService.dismissNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {} finally { setDismissingId(null); }
  };

  const handleScan = async () => {
    setScanning(true);
    try { await CuratorService.scanSystemic(clientId); await load(); } catch {} finally { setScanning(false); }
  };

  const priorityColor = (p: string) =>
    p === 'high' ? theme.palette.error.main : p === 'medium' ? theme.palette.warning.main : theme.palette.info.main;

  const high = notifications.filter(n => n.priority === 'high');
  const rest = notifications.filter(n => n.priority !== 'high');
  const sorted = [...high, ...rest];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button size="small" disabled={scanning} onClick={handleScan} startIcon={scanning ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, height: 28, textTransform: 'none' }}>
          Scan for issues
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
      ) : !sorted.length ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>No pending notifications — AI quality is on track</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {sorted.map(n => {
            const color = priorityColor(n.priority);
            return (
              <Box key={n.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: 1, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.border.light}`, borderLeft: `3px solid ${color}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.375 }}>
                    <Chip label={n.priority.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, color, backgroundColor: colorUtils.withOpacity(color, 0.12) }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', ml: 'auto', flexShrink: 0 }}>
                      {getRelativeTimeString(n.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontSize: '0.78rem', lineHeight: 1.4 }}>{n.message}</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {n.session_id && (
                      <Button size="small" onClick={() => navigate(`/playbooks/sessions/${n.session_id}`)} sx={{ mt: 0.375, fontSize: '0.68rem', color: theme.palette.primary.main, px: 0, height: 22, textTransform: 'none', minWidth: 'auto' }}>
                        View case →
                      </Button>
                    )}
                    {n.ticket_id && (
                      <Button size="small" onClick={() => onViewTrace(n.ticket_id!)} sx={{ mt: 0.375, fontSize: '0.68rem', color: theme.palette.text.secondary, px: 0, height: 22, textTransform: 'none', minWidth: 'auto' }}>
                        View trace →
                      </Button>
                    )}
                    {NOTIFICATION_ACTION_TAB[n.notification_type] && (
                      <Button
                        size="small"
                        onClick={() => onGoToTab(NOTIFICATION_ACTION_TAB[n.notification_type].tab)}
                        sx={{ mt: 0.375, fontSize: '0.68rem', fontWeight: 600, color: theme.palette.warning.main, px: 0, height: 22, textTransform: 'none', minWidth: 'auto' }}
                      >
                        {NOTIFICATION_ACTION_TAB[n.notification_type].label}
                      </Button>
                    )}
                  </Box>
                </Box>
                <Tooltip title="Dismiss">
                  <span>
                    <IconButton size="small" disabled={dismissingId === n.id} onClick={() => handleDismiss(n.id)} sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.text.secondary }, flexShrink: 0 }}>
                      {dismissingId === n.id ? <CircularProgress size={12} /> : <CloseIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ── Coverage tab ──────────────────────────────────────────────────────────────

const CoverageTab: React.FC<{ clientId: string; onViewTrace: (ticketId: string) => void }> = ({ clientId, onViewTrace }) => {
  const theme = useTheme();
  const [gaps, setGaps] = useState<RecallGap[]>([]);
  const [lowRecall, setLowRecall] = useState<LowRecallTicket[]>([]);
  const [missed, setMissed] = useState<MissedChunk[]>([]);
  const [loading, setLoading] = useState(false);
  const [flaggingId, setFlaggingId] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    Promise.all([
      CuratorService.getRecallGaps(clientId),
      CuratorService.getLowRecallTickets(clientId),
      CuratorService.getMissedChunks(clientId),
    ]).then(([g, l, m]) => {
      setGaps(g.gaps ?? []);
      setLowRecall(l.tickets ?? []);
      setMissed(m.chunks ?? []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const handleFlag = async (memoryId: string) => {
    setFlaggingId(memoryId);
    try {
      await CuratorService.flagSystemic(memoryId);
      setMissed(prev => prev.map(m => m.memory_id === memoryId ? { ...m, is_systemic: true } : m));
    } catch {} finally { setFlaggingId(null); }
  };

  const SLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1.25, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>{children}</Typography>
  );

  const gapCols: Column<RecallGap>[] = [
    {
      key: 'incident_type', header: 'Incident type', align: 'left', minWidth: 160, sortable: true,
      sortValue: (r) => r.incident_type,
      render: (r) => <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{r.incident_type}</Typography>,
    },
    {
      key: 'golden_entry_count', header: 'Golden entries', align: 'right', minWidth: 130, sortable: true,
      sortValue: (r) => r.golden_entry_count,
      render: (r) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: r.golden_entry_count < 3 ? theme.palette.warning.main : theme.palette.text.secondary }}>
          {r.golden_entry_count}
        </Typography>
      ),
    },
    {
      key: 'avg_recall', header: 'Avg coverage', align: 'right', minWidth: 120, sortable: true,
      sortValue: (r) => r.avg_recall,
      render: (r) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: r.avg_recall == null ? theme.palette.text.disabled : r.avg_recall < 0.75 ? theme.palette.error.main : theme.palette.success.main }}>
          {r.avg_recall != null ? `${Math.round(r.avg_recall * 100)}%` : '—'}
        </Typography>
      ),
    },
    {
      key: 'ticket_count', header: 'Tickets', align: 'right', minWidth: 90, sortable: true,
      sortValue: (r) => r.ticket_count,
      render: (r) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: theme.palette.text.secondary }}>{r.ticket_count}</Typography>,
    },
  ];

  const lowRecallCols: Column<LowRecallTicket>[] = [
    {
      key: 'ticket_title', header: 'Ticket', align: 'left', minWidth: 220,
      render: (r) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 320 }}>
          {r.ticket_title}
        </Typography>
      ),
    },
    {
      key: 'incident_type', header: 'Type', align: 'left', minWidth: 110,
      render: (r) => r.incident_type ? (
        <Chip label={r.incident_type.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.secondary }} />
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>—</Typography>
      ),
    },
    {
      key: 'recall', header: 'Coverage', align: 'right', minWidth: 100, sortable: true,
      sortValue: (r) => r.recall,
      render: (r) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: r.recall != null ? theme.palette.error.main : theme.palette.text.disabled }}>
          {r.recall != null ? `${Math.round(r.recall * 100)}%` : '—'}
        </Typography>
      ),
    },
    {
      key: 'created_at', header: 'Date', align: 'right', minWidth: 110, sortable: true,
      sortValue: (r) => r.created_at,
      render: (r) => <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.72rem' }}>{getRelativeTimeString(r.created_at)}</Typography>,
    },
    {
      key: 'actions', header: '', align: 'right', minWidth: 80,
      render: (r) => (
        <Button size="small" onClick={() => onViewTrace(r.ticket_id)} sx={{ fontSize: '0.7rem', fontWeight: 600, color: theme.palette.primary.main, px: 0, height: 24, textTransform: 'none', minWidth: 'auto' }}>
          Trace →
        </Button>
      ),
    },
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Coverage gaps */}
      <Box>
        <SLabel>Coverage Gaps</SLabel>
        {!gaps.length ? (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>No gaps detected</Typography>
        ) : (
          <DataTable<RecallGap>
            columns={gapCols}
            data={gaps}
            rowKey={(r) => r.incident_type}
            showPagination={false}
            minTableWidth={480}
            rowHeight={44}
          />
        )}
      </Box>

      {/* Low coverage tickets */}
      <Box>
        <SLabel>Low Coverage Tickets</SLabel>
        {!lowRecall.length ? (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>No low-coverage tickets</Typography>
        ) : (
          <DataTable<LowRecallTicket>
            columns={lowRecallCols}
            data={lowRecall}
            rowKey={(r) => r.ticket_id}
            defaultRowsPerPage={5}
            rowsPerPageOptions={[5, 10, 25]}
            minTableWidth={620}
            rowHeight={44}
          />
        )}
      </Box>

      {/* Frequently missed past cases */}
      <Box>
        <SLabel>Frequently Missed Past Cases</SLabel>
        {!missed.length ? (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>No systemic misses detected</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {missed.map(m => (
              <Box key={m.memory_id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.border.light}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.375 }}>
                    {m.incident_type && <Chip label={m.incident_type.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />}
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem' }}>missed {m.missed_count}×</Typography>
                    {m.is_systemic && <Chip label="SYSTEMIC" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, color: theme.palette.error.main, backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.1) }} />}
                  </Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', lineHeight: 1.4 }}>{m.content_preview}</Typography>
                </Box>
                {!m.is_systemic && (
                  <Tooltip title="Flag as systemic retrieval problem → developer queue">
                    <span>
                      <Button size="small" disabled={flaggingId === m.memory_id} onClick={() => handleFlag(m.memory_id)} sx={{ fontSize: '0.68rem', color: theme.palette.error.main, px: 1, height: 26, textTransform: 'none', border: `1px solid ${colorUtils.withOpacity(theme.palette.error.main, 0.3)}`, borderRadius: 1, flexShrink: 0 }}>
                        {flaggingId === m.memory_id ? <CircularProgress size={10} color="inherit" /> : 'Flag'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Enhanced run validation dialog ────────────────────────────────────────────

interface RunValidationDialogProps {
  entry: GoldenSetEntry | null;
  onClose: () => void;
}

const RunValidationDialog: React.FC<RunValidationDialogProps> = ({ entry, onClose }) => {
  const theme = useTheme();
  const [results, setResults] = useState<GoldenSetRunResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [validatingRun, setValidatingRun] = useState<GoldenSetRunResult | null>(null);

  // Validation state
  const [trace, setTrace] = useState<PipelineTrace | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [memories, setMemories] = useState<IncidentMemory[]>([]);
  const [chunkVerdicts, setChunkVerdicts] = useState<Record<string, 'relevant' | 'not_relevant'>>({});
  const [stepVerdicts, setStepVerdicts] = useState<Record<number, { verdict: 'correct' | 'wrong' | 'partial'; edited?: string }>>({});
  const [editedSummary, setEditedSummary] = useState('');
  const [savingCStar, setSavingCStar] = useState(false);
  const [savingBaseline, setSavingBaseline] = useState(false);

  // Hint state
  const [hintOpen, setHintOpen] = useState(false);
  const [hintMemoryId, setHintMemoryId] = useState('');
  const [hintText, setHintText] = useState('');
  const [hintResult, setHintResult] = useState<{ new_summary: string | null; new_next_steps: { step: string; rationale: string }[] } | null>(null);
  const [submittingHint, setSubmittingHint] = useState(false);
  const [gradingId, setGradingId] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    setLoading(true);
    PlaybooksService.getGoldenSetResults(entry.id)
      .then(r => setResults(r.results))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [entry?.id]);

  // Load trace + memories when a run is selected for validation
  useEffect(() => {
    if (!validatingRun || !entry) return;
    setTraceLoading(true);
    setTrace(null);
    setMemories([]);
    setChunkVerdicts({});
    setStepVerdicts({});
    setEditedSummary(entry.baseline_summary ?? '');
    setHintResult(null);

    CuratorService.getRunTrace(entry.id, validatingRun.id)
      .then(async (t) => {
        setTrace(t);
        if (t.retrieved_ids?.length) {
          const mems = await CuratorService.getMemories(t.retrieved_ids, entry.client_id);
          setMemories(mems);
        }
      })
      .catch(() => {})
      .finally(() => setTraceLoading(false));
  }, [validatingRun?.id, entry?.id]);

  const handleGrade = async (resultId: string, verdict: 'approved' | 'rejected') => {
    if (!entry) return;
    setGradingId(resultId);
    try {
      const updated = await PlaybooksService.gradeGoldenSetResult(entry.id, resultId, verdict);
      setResults(prev => prev.map(r => r.id === resultId ? updated : r));
    } finally { setGradingId(null); }
  };

  const handleSaveCStar = async () => {
    if (!entry) return;
    const add = Object.entries(chunkVerdicts).filter(([, v]) => v === 'relevant').map(([id]) => id);
    const remove = Object.entries(chunkVerdicts).filter(([, v]) => v === 'not_relevant').map(([id]) => id);
    setSavingCStar(true);
    try { await CuratorService.updateCStar(entry.id, { add, remove }); } catch {} finally { setSavingCStar(false); }
  };

  const handleSaveBaseline = async () => {
    if (!entry || !validatingRun) return;
    // Build revised next_steps from verdicts
    const aiSteps = validatingRun.new_next_steps ?? [];
    const revisedSteps = aiSteps
      .map((s, idx) => {
        const v = stepVerdicts[idx];
        if (!v || v.verdict === 'wrong') return null;
        return { step: v.edited ?? s.step, rationale: s.rationale };
      })
      .filter(Boolean) as { step: string; rationale: string }[];
    setSavingBaseline(true);
    try { await CuratorService.updateBaseline(entry.id, { baseline_summary: editedSummary, baseline_next_steps: revisedSteps }); } catch {} finally { setSavingBaseline(false); }
  };

  const handleSubmitHint = async () => {
    if (!entry || !hintMemoryId.trim()) return;
    setSubmittingHint(true);
    setHintResult(null);
    try {
      const res = await CuratorService.addHint(entry.id, { memory_id: hintMemoryId.trim(), hint_text: hintText.trim() || undefined });
      setHintResult(res);
    } catch {} finally { setSubmittingHint(false); }
  };

  const handleAcceptHint = async () => {
    if (!entry || !hintResult) return;
    setSavingBaseline(true);
    try {
      await CuratorService.updateBaseline(entry.id, {
        baseline_summary: hintResult.new_summary ?? editedSummary,
        baseline_next_steps: hintResult.new_next_steps,
      });
      setHintOpen(false);
      setHintResult(null);
      setHintMemoryId('');
      setHintText('');
    } catch {} finally { setSavingBaseline(false); }
  };

  const scoreColor = (score: number | null) => {
    if (score == null) return theme.palette.text.disabled;
    if (score >= 0.8) return theme.palette.success.main;
    if (score >= 0.6) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const stepVerdictIcon = (v?: string) => {
    if (v === 'correct') return <CorrectIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />;
    if (v === 'wrong') return <WrongIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />;
    if (v === 'partial') return <PartialIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
    return null;
  };

  // ── Validation view ────────────────────────────────────────────────────────
  if (validatingRun && entry) {
    const aiSteps = validatingRun.new_next_steps ?? [];
    return (
      <Dialog open maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setValidatingRun(null)} sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>
              <BackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Validate Run</Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block' }}>
                {entry.name || `Session ${entry.session_id.slice(0, 8)}…`} · {getRelativeTimeString(validatingRun.run_at)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {traceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Past incidents used (retrieved chunks) */}
              {memories.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                    Past incidents used as context
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
                    {memories.map(m => {
                      const verdict = chunkVerdicts[m.id];
                      return (
                        <Box key={m.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${verdict === 'relevant' ? theme.palette.success.main : verdict === 'not_relevant' ? theme.palette.error.main : theme.palette.border.light}` }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mb: 0.375 }}>
                              {m.incident_type && <Chip label={m.incident_type.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />}
                              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', ml: 'auto' }}>{getRelativeTimeString(m.created_at)}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', lineHeight: 1.4 }}>
                              {m.content_text.length > 160 ? m.content_text.slice(0, 160) + '…' : m.content_text}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, flexShrink: 0 }}>
                            <Button size="small" onClick={() => setChunkVerdicts(prev => ({ ...prev, [m.id]: 'relevant' }))} sx={{ fontSize: '0.65rem', px: 0.875, height: 22, textTransform: 'none', color: verdict === 'relevant' ? theme.palette.success.main : theme.palette.text.tertiary, border: `1px solid ${verdict === 'relevant' ? theme.palette.success.main : theme.palette.border.light}`, borderRadius: 1 }}>
                              Relevant
                            </Button>
                            <Button size="small" onClick={() => setChunkVerdicts(prev => ({ ...prev, [m.id]: 'not_relevant' }))} sx={{ fontSize: '0.65rem', px: 0.875, height: 22, textTransform: 'none', color: verdict === 'not_relevant' ? theme.palette.error.main : theme.palette.text.tertiary, border: `1px solid ${verdict === 'not_relevant' ? theme.palette.error.main : theme.palette.border.light}`, borderRadius: 1 }}>
                              Not relevant
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Summary comparison with word-diff */}
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                  Summary — AI answer vs baseline
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box sx={{ p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.5 }}>AI answer</Typography>
                    <Box sx={{ fontSize: '0.72rem', lineHeight: 1.5, color: theme.palette.text.secondary }}>
                      {(validatingRun.new_summary && editedSummary)
                        ? diffWords(editedSummary, validatingRun.new_summary).map((chunk, i) => (
                            <span key={i} style={{
                              backgroundColor: chunk.type === 'add' ? colorUtils.withOpacity(theme.palette.success.main, 0.18) : chunk.type === 'del' ? colorUtils.withOpacity(theme.palette.error.main, 0.18) : undefined,
                              color: chunk.type === 'add' ? theme.palette.success.main : chunk.type === 'del' ? theme.palette.error.main : theme.palette.text.secondary,
                              textDecoration: chunk.type === 'del' ? 'line-through' : undefined,
                            }}>{chunk.text}</span>
                          ))
                        : <span>{validatingRun.new_summary ?? '—'}</span>
                      }
                    </Box>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.5 }}>Baseline (editable)</Typography>
                    <TextField
                      value={editedSummary}
                      onChange={e => setEditedSummary(e.target.value)}
                      multiline
                      minRows={3}
                      size="small"
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.72rem', p: 0, '& fieldset': { border: 'none' }, '& textarea': { lineHeight: 1.5 } } }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Step-level validation */}
              {aiSteps.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                    Next steps — mark each recommendation
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {aiSteps.map((s, idx) => {
                      const sv = stepVerdicts[idx];
                      const isEditing = sv?.verdict === 'partial';
                      const fv = (validatingRun.details?.faithfulness ?? []).find((v: any) => v.step_index === idx);
                      return (
                        <Box key={idx} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${sv?.verdict === 'correct' ? theme.palette.success.main : sv?.verdict === 'wrong' ? theme.palette.error.main : sv?.verdict === 'partial' ? theme.palette.warning.main : theme.palette.border.light}` }}>
                          <Typography variant="caption" sx={{ minWidth: 18, height: 18, borderRadius: '50%', background: theme.palette.background.paper, border: `1px solid ${theme.palette.border.main}`, color: theme.palette.text.tertiary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, mt: 0.1, flexShrink: 0 }}>
                            {idx + 1}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isEditing ? (
                              <TextField
                                value={sv.edited ?? s.step}
                                onChange={e => setStepVerdicts(prev => ({ ...prev, [idx]: { verdict: 'partial', edited: e.target.value } }))}
                                size="small"
                                fullWidth
                                multiline
                                maxRows={3}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', backgroundColor: theme.palette.background.paper, '& fieldset': { borderColor: theme.palette.border.light } } }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', color: sv?.verdict === 'wrong' ? theme.palette.text.disabled : theme.palette.text.primary, textDecoration: sv?.verdict === 'wrong' ? 'line-through' : 'none', lineHeight: 1.4 }}>
                                {s.step}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, flexShrink: 0, mt: 0.1 }}>
                            {fv && (
                              <>
                                <Tooltip title={`AI faithfulness check — ${fv.verdict === 'grounded' ? 'grounded in evidence' : 'not clearly grounded'}: ${fv.reason}`}>
                                  <Chip
                                    size="small"
                                    icon={fv.verdict === 'grounded'
                                      ? <GroundedIcon sx={{ fontSize: '12px !important' }} />
                                      : <UngroundedIcon sx={{ fontSize: '12px !important' }} />}
                                    label={fv.verdict === 'grounded' ? 'Grounded' : 'Ungrounded'}
                                    sx={{
                                      height: 18, fontSize: '0.58rem', fontWeight: 600, textTransform: 'uppercase',
                                      color: fv.verdict === 'grounded' ? theme.palette.info.main : theme.palette.warning.main,
                                      backgroundColor: colorUtils.withOpacity(fv.verdict === 'grounded' ? theme.palette.info.main : theme.palette.warning.main, 0.1),
                                      border: `1px dashed ${fv.verdict === 'grounded' ? theme.palette.info.main : theme.palette.warning.main}`,
                                      '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                                    }}
                                  />
                                </Tooltip>
                                <Box sx={{ width: '1px', height: 16, backgroundColor: theme.palette.border.light, flexShrink: 0, mx: 0.25 }} />
                              </>
                            )}
                            <Tooltip title="Correct">
                              <IconButton size="small" onClick={() => setStepVerdicts(prev => ({ ...prev, [idx]: { verdict: 'correct' } }))} sx={{ p: 0.25, color: sv?.verdict === 'correct' ? theme.palette.success.main : theme.palette.text.disabled }}>
                                <CorrectIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Partially correct — click to edit">
                              <IconButton size="small" onClick={() => setStepVerdicts(prev => ({ ...prev, [idx]: { verdict: 'partial', edited: prev[idx]?.edited ?? s.step } }))} sx={{ p: 0.25, color: sv?.verdict === 'partial' ? theme.palette.warning.main : theme.palette.text.disabled }}>
                                <PartialIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Wrong — remove from baseline">
                              <IconButton size="small" onClick={() => setStepVerdicts(prev => ({ ...prev, [idx]: { verdict: 'wrong' } }))} sx={{ p: 0.25, color: sv?.verdict === 'wrong' ? theme.palette.error.main : theme.palette.text.disabled }}>
                                <WrongIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Hint modal */}
              {hintOpen && (
                <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${theme.palette.border.main}`, backgroundColor: theme.palette.background.paper }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    <HintIcon sx={{ fontSize: 15, color: theme.palette.warning.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem' }}>Add hint — inject a past case and re-run</Typography>
                    <IconButton size="small" onClick={() => { setHintOpen(false); setHintResult(null); }} sx={{ ml: 'auto', color: theme.palette.text.disabled }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  {trace != null && (trace.missed_chunks?.length ?? 0) > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>Missed past cases (click to select)</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {trace.missed_chunks.map(chunk => (
                          <Chip
                            key={chunk.id}
                            label={chunk.incident_type ? `${chunk.incident_type} — ${chunk.content_preview?.slice(0, 30) ?? chunk.id.slice(0, 8)}…` : `${chunk.id.slice(0, 8)}…`}
                            size="small"
                            onClick={() => setHintMemoryId(chunk.id)}
                            sx={{ height: 18, fontSize: '0.6rem', cursor: 'pointer', maxWidth: 220, backgroundColor: hintMemoryId === chunk.id ? colorUtils.withOpacity(theme.palette.primary.main, 0.15) : theme.palette.background.tertiary, border: `1px solid ${hintMemoryId === chunk.id ? theme.palette.primary.main : theme.palette.border.light}` }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <TextField label="Past case ID" value={hintMemoryId} onChange={e => setHintMemoryId(e.target.value)} size="small" fullWidth sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.78rem', fontFamily: 'monospace', backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.light } } }} />
                  <TextField label="Note (optional)" value={hintText} onChange={e => setHintText(e.target.value)} size="small" fullWidth multiline minRows={2} sx={{ mb: 1.25, '& .MuiOutlinedInput-root': { fontSize: '0.78rem', backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.border.light } } }} />
                  <Button size="small" variant="contained" disabled={submittingHint || !hintMemoryId.trim()} onClick={handleSubmitHint} sx={{ fontSize: '0.72rem', px: 1.5, height: 28, textTransform: 'none' }}>
                    {submittingHint ? <CircularProgress size={11} color="inherit" /> : 'Re-run with hint'}
                  </Button>

                  {/* Hint result — show before/after side by side */}
                  {hintResult && (
                    <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${theme.palette.border.light}` }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.875 }}>Result</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.25 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.375 }}>Before hint</Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{validatingRun.new_summary ?? '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.success.main, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.375 }}>After hint</Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{hintResult.new_summary ?? '—'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Button size="small" variant="contained" disabled={savingBaseline} onClick={handleAcceptHint} sx={{ fontSize: '0.72rem', px: 1.5, height: 28, textTransform: 'none' }}>
                          {savingBaseline ? <CircularProgress size={11} color="inherit" /> : 'Accept as new baseline'}
                        </Button>
                        <Button size="small" onClick={() => setHintResult(null)} sx={{ fontSize: '0.72rem', px: 1, height: 28, textTransform: 'none', color: theme.palette.text.tertiary }}>Try again</Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
          {!hintOpen && (
            <Button size="small" startIcon={<HintIcon sx={{ fontSize: 14 }} />} onClick={() => setHintOpen(true)} sx={{ fontSize: '0.72rem', color: theme.palette.warning.main, height: 30, textTransform: 'none', mr: 'auto' }}>
              Add hint
            </Button>
          )}
          <Button size="small" disabled={savingCStar || !Object.keys(chunkVerdicts).length} onClick={handleSaveCStar} sx={{ fontSize: '0.72rem', height: 30, textTransform: 'none', color: theme.palette.text.secondary }}>
            {savingCStar ? <CircularProgress size={11} color="inherit" /> : 'Save relevant cases'}
          </Button>
          <Button size="small" variant="contained" disabled={savingBaseline} onClick={handleSaveBaseline} sx={{ fontSize: '0.72rem', px: 1.5, height: 30, textTransform: 'none' }}>
            {savingBaseline ? <CircularProgress size={11} color="inherit" /> : 'Save baseline'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Run list view ──────────────────────────────────────────────────────────
  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Benchmark Run History</Typography>
        {entry && (
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mt: 0.25 }}>
            {entry.name || `Session ${entry.session_id.slice(0, 8)}…`}
            {entry.snapshot_incident_type && ` · ${entry.snapshot_incident_type}`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : !results.length ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>No runs yet — click "Run" on this entry to generate a benchmark result</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 70px 70px 70px 70px 70px 70px 1fr 80px', gap: 0, px: 0.5 }}>
              {['Run date', 'Summary', 'Coverage', 'Halluc.', 'Faithful.', 'Score', 'Result', 'Grade', ''].map(h => (
                <Typography key={h} variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.05em', textTransform: 'uppercase', pb: 0.75, borderBottom: `1px solid ${theme.palette.border.light}` }}>{h}</Typography>
              ))}
            </Box>
            {results.map(r => {
              const isGrading = gradingId === r.id;
              return (
                <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: '140px 70px 70px 70px 70px 70px 70px 1fr 80px', gap: 0, px: 0.5, alignItems: 'center', py: 0.5, borderBottom: `1px solid ${theme.palette.border.light}` }}>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>{getRelativeTimeString(r.run_at)}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: scoreColor(r.summary_similarity) }}>{r.summary_similarity != null ? r.summary_similarity.toFixed(2) : '—'}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: scoreColor(r.coverage_score) }}>{r.coverage_score != null ? r.coverage_score.toFixed(2) : '—'}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: (r.hallucination_risk_count ?? 0) > 2 ? theme.palette.error.main : theme.palette.text.secondary }}>{r.hallucination_risk_count ?? '—'}</Typography>
                  <Tooltip title="Faithfulness — fraction of next steps the AI judge found grounded in the incident's evidence and retrieved past cases (advisory, not part of pass/fail)">
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: scoreColor(r.faithfulness_score) }}>{r.faithfulness_score != null ? r.faithfulness_score.toFixed(2) : '—'}</Typography>
                  </Tooltip>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.72rem', color: scoreColor(r.overall_score) }}>{r.overall_score != null ? r.overall_score.toFixed(2) : '—'}</Typography>
                  <Box>
                    {r.passed != null ? (
                      <Chip label={r.passed ? 'PASS' : 'FAIL'} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, color: r.passed ? theme.palette.success.main : theme.palette.error.main, backgroundColor: colorUtils.withOpacity(r.passed ? theme.palette.success.main : theme.palette.error.main, 0.1) }} />
                    ) : <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>—</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {r.analyst_verdict ? (
                      <Chip label={r.analyst_verdict.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, color: r.analyst_verdict === 'approved' ? theme.palette.success.main : theme.palette.error.main, backgroundColor: colorUtils.withOpacity(r.analyst_verdict === 'approved' ? theme.palette.success.main : theme.palette.error.main, 0.1) }} />
                    ) : (
                      <>
                        <Tooltip title="Approve">
                          <span>
                            <IconButton size="small" disabled={isGrading} onClick={() => handleGrade(r.id, 'approved')} sx={{ color: theme.palette.success.main, p: 0.25, '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.1) } }}>
                              {isGrading ? <CircularProgress size={12} /> : <ApproveIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <span>
                            <IconButton size="small" disabled={isGrading} onClick={() => handleGrade(r.id, 'rejected')} sx={{ color: theme.palette.error.main, p: 0.25, '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.1) } }}>
                              <RejectGradeIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setValidatingRun(r)} sx={{ fontSize: '0.68rem', color: theme.palette.primary.main, px: 0.75, height: 22, textTransform: 'none' }}>
                      Validate
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Deployment status banner ──────────────────────────────────────────────────

const DeploymentBanner: React.FC<{ report: GoldenSetReport }> = ({ report }) => {
  const theme = useTheme();
  const cfg = {
    healthy:           { color: theme.palette.success.main,  label: 'Deployment Ready',       icon: <PassedIcon sx={{ fontSize: 18 }} /> },
    warning:           { color: theme.palette.warning.main,  label: 'Warning — Review Needed', icon: <NoDataIcon sx={{ fontSize: 18 }} /> },
    failing:           { color: theme.palette.error.main,    label: 'Failing — Not Ready',     icon: <FailedIcon sx={{ fontSize: 18 }} /> },
    insufficient_data: { color: theme.palette.text.disabled, label: 'Insufficient Data',        icon: <NoDataIcon sx={{ fontSize: 18 }} /> },
  }[report.deployment_status];

  return (
    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ color: theme.palette.text.tertiary, display: 'flex' }}>{cfg.icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: cfg.color }}>{cfg.label}</Typography>
        {report.blocked_reason && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{report.blocked_reason}</Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Entries', value: String(report.total_entries) },
          { label: 'Pass rate', value: report.pass_rate != null ? `${Math.round(report.pass_rate * 100)}%` : '—' },
          { label: 'Avg score', value: report.avg_score != null ? report.avg_score.toFixed(2) : '—' },
          { label: 'Analyst override', value: report.analyst_override_rate != null ? `${Math.round(report.analyst_override_rate * 100)}%` : '—' },
        ].map(({ label, value }) => (
          <Box key={label} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'monospace', color: theme.palette.text.primary, lineHeight: 1.2 }}>{value}</Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const AIGymPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clients } = useSelector((state: RootState) => state.clients);

  const [searchParams, setSearchParams] = useSearchParams();
  const [clientId, setClientIdState] = useState(() => searchParams.get('client') || '');
  const setClientId = useCallback((id: string) => {
    setClientIdState(id);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('client', id); else next.delete('client');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [tab, setTab] = useState(0);

  const [entries, setEntries] = useState<GoldenSetEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const [candidates, setCandidates] = useState<GoldenSetCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  const [report, setReport] = useState<GoldenSetReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [runningId, setRunningId] = useState<string | null>(null);
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [curateTarget, setCurateTarget] = useState<GoldenSetCandidate | null>(null);
  const [resultsTarget, setResultsTarget] = useState<GoldenSetEntry | null>(null);
  const [traceTicketId, setTraceTicketId] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  const loadEntries = useCallback(async (cid: string) => {
    setEntriesLoading(true);
    try {
      const res = await PlaybooksService.getGoldenSetEntries(cid);
      setEntries(res.entries);
    } catch { setEntries([]); }
    finally { setEntriesLoading(false); }
  }, []);

  const loadCandidates = useCallback(async (cid: string) => {
    setCandidatesLoading(true);
    try {
      const res = await PlaybooksService.getGoldenSetCandidates(cid);
      setCandidates(res.candidates);
    } catch { setCandidates([]); }
    finally { setCandidatesLoading(false); }
  }, []);

  const loadReport = useCallback(async (cid: string) => {
    setReportLoading(true);
    try {
      const res = await PlaybooksService.getGoldenSetReport(cid);
      setReport(res);
    } catch { setReport(null); }
    finally { setReportLoading(false); }
  }, []);

  const loadAll = useCallback((cid: string) => {
    loadEntries(cid);
    loadCandidates(cid);
    loadReport(cid);
  }, [loadEntries, loadCandidates, loadReport]);

  useEffect(() => {
    if (clientId) loadAll(clientId);
    else { setEntries([]); setCandidates([]); setReport(null); }
  }, [clientId]);

  const handleRun = async (entryId: string) => {
    setRunningId(entryId);
    try {
      const result = await PlaybooksService.runGoldenSetEntry(entryId);
      const passed = result.passed ? 'passed' : 'failed';
      const score = result.overall_score != null ? ` (score: ${result.overall_score.toFixed(2)})` : '';
      setToastMsg(`Benchmark ${passed}${score}`);
      setTimeout(() => setToastMsg(null), 4000);
      if (clientId) { loadEntries(clientId); loadReport(clientId); }
    } catch (e: any) {
      setToastMsg(`Run failed: ${e.message}`);
      setTimeout(() => setToastMsg(null), 4000);
    } finally {
      setRunningId(null);
    }
  };

  const handleRunAll = async () => {
    if (!clientId) return;
    setRunAllLoading(true);
    try {
      await PlaybooksService.runAllGoldenSet(clientId);
      setToastMsg('Benchmark run started — poll report to see results');
      setTimeout(() => setToastMsg(null), 4000);
    } catch (e: any) {
      setToastMsg(`Run all failed: ${e.message}`);
      setTimeout(() => setToastMsg(null), 4000);
    } finally {
      setRunAllLoading(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    try {
      await PlaybooksService.deleteGoldenSetEntry(entryId);
      if (clientId) { loadEntries(clientId); loadReport(clientId); }
    } catch (e: any) {
      setToastMsg(`Delete failed: ${e.message}`);
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const handleCurated = () => {
    setCurateTarget(null);
    if (clientId) loadAll(clientId);
    setToastMsg('Session added to golden set');
    setTimeout(() => setToastMsg(null), 3000);
  };

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1.5, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
      {children}
    </Typography>
  );

  const passedChipSx = (passed: boolean | null) => {
    if (passed === null) return { height: 18, fontSize: '0.6rem', fontWeight: 700, color: theme.palette.text.disabled, backgroundColor: theme.palette.background.tertiary };
    const color = passed ? theme.palette.success.main : theme.palette.error.main;
    return { height: 18, fontSize: '0.6rem', fontWeight: 700, color, backgroundColor: colorUtils.withOpacity(color, 0.1), border: `1px solid ${colorUtils.withOpacity(color, 0.3)}` };
  };

  const entryCols: Column<GoldenSetEntry>[] = [
    {
      key: 'name',
      header: 'Name',
      minWidth: 180,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem', color: theme.palette.text.primary }}>
          {row.name || `Session ${row.session_id.slice(0, 8)}…`}
        </Typography>
      ),
    },
    {
      key: 'snapshot_incident_type',
      header: 'Type',
      minWidth: 80,
      align: 'left',
      render: (row) => row.snapshot_incident_type ? (
        <Chip label={row.snapshot_incident_type.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>IOC</Typography>
      ),
    },
    {
      key: 'quality_score',
      header: 'Quality',
      minWidth: 80,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: theme.palette.text.secondary }}>
          {row.quality_score != null ? `${(row.quality_score * 100).toFixed(0)}%` : '—'}
        </Typography>
      ),
    },
    {
      key: 'last_score',
      header: 'Last score',
      minWidth: 90,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: theme.palette.text.secondary }}>
          {row.last_score != null ? row.last_score.toFixed(2) : '—'}
        </Typography>
      ),
    },
    {
      key: 'last_passed',
      header: 'Result',
      minWidth: 80,
      align: 'center',
      render: (row) => row.last_run_at ? (
        <Chip
          label={row.last_passed ? 'PASSED' : 'FAILED'}
          icon={row.last_passed ? <PassedIcon sx={{ fontSize: 11 }} /> : <FailedIcon sx={{ fontSize: 11 }} />}
          size="small"
          sx={passedChipSx(row.last_passed)}
        />
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>Never run</Typography>
      ),
    },
    {
      key: 'last_run_at',
      header: 'Last run',
      minWidth: 120,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {row.last_run_at ? getRelativeTimeString(row.last_run_at) : '—'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: '',
      minWidth: 148,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
          <Tooltip title="Open session">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/playbooks/sessions/${row.session_id}`); }} sx={{ color: theme.palette.text.tertiary, '&:hover': { color: theme.palette.primary.main } }}>
              <OpenIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.last_run_at ? 'View & grade run history' : 'No runs yet'}>
            <span>
              <IconButton
                size="small"
                disabled={!row.last_run_at}
                onClick={(e) => { e.stopPropagation(); setResultsTarget(row); }}
                sx={{ color: row.last_run_at ? theme.palette.primary.main : theme.palette.text.disabled, '&:hover': { color: theme.palette.primary.light } }}
              >
                <HistoryIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Run benchmark">
            <span>
              <IconButton
                size="small"
                disabled={runningId === row.id}
                onClick={(e) => { e.stopPropagation(); handleRun(row.id); }}
                sx={{ color: theme.palette.text.tertiary, '&:hover': { color: theme.palette.info.main } }}
              >
                {runningId === row.id ? <CircularProgress size={12} /> : <RunIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete from golden set">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }} sx={{ color: theme.palette.text.tertiary, '&:hover': { color: theme.palette.error.main } }}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const candidateCols: Column<GoldenSetCandidate>[] = [
    {
      key: 'incident_type',
      header: 'Type',
      minWidth: 90,
      align: 'left',
      render: (row) => row.incident_type ? (
        <Chip label={row.incident_type.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>IOC</Typography>
      ),
    },
    {
      key: 'quality_score',
      header: 'Quality',
      minWidth: 80,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: row.quality_score >= 0.8 ? theme.palette.success.main : row.quality_score >= 0.6 ? theme.palette.warning.main : theme.palette.error.main }}>
          {(row.quality_score * 100).toFixed(0)}%
        </Typography>
      ),
    },
    {
      key: 'acceptance_rate',
      header: 'Acceptance',
      minWidth: 90,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: theme.palette.text.secondary }}>
          {row.acceptance_rate != null ? `${(row.acceptance_rate * 100).toFixed(0)}%` : '—'}
        </Typography>
      ),
    },
    {
      key: 'steps_done',
      header: 'Steps',
      minWidth: 70,
      align: 'center',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: theme.palette.text.secondary }}>
          {row.steps_done}/{row.steps_total}
        </Typography>
      ),
    },
    {
      key: 'has_root_cause',
      header: 'Root cause',
      minWidth: 110,
      align: 'center',
      render: (row) => row.has_root_cause && row.root_cause ? (
        <Tooltip title={row.root_cause} placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: theme.palette.success.main, cursor: 'default' }}>
            <PassedIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.root_cause}
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ color: theme.palette.text.disabled, display: 'flex', justifyContent: 'center' }}>
          <FailedIcon sx={{ fontSize: 14 }} />
        </Box>
      ),
    },
    {
      key: 'has_mitre_tags',
      header: 'MITRE tags',
      minWidth: 130,
      align: 'center',
      render: (row) => row.mitre_tags?.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.375, flexWrap: 'wrap' }}>
          {row.mitre_tags.slice(0, 2).map(tag => (
            <Chip key={tag} label={tag} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, fontFamily: 'monospace', backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.secondary }} />
          ))}
          {row.mitre_tags.length > 2 && (
            <Tooltip title={row.mitre_tags.slice(2).join(', ')}>
              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: theme.palette.text.disabled, alignSelf: 'center' }}>+{row.mitre_tags.length - 2}</Typography>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box sx={{ color: theme.palette.text.disabled, display: 'flex', justifyContent: 'center' }}>
          <FailedIcon sx={{ fontSize: 14 }} />
        </Box>
      ),
    },
    {
      key: 'created_at',
      header: 'Session date',
      minWidth: 120,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {row.created_at ? getRelativeTimeString(row.created_at) : '—'}
        </Typography>
      ),
    },
    {
      key: 'curate',
      header: '',
      minWidth: 100,
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); setCurateTarget(row); }}
          startIcon={<AddIcon sx={{ fontSize: 12 }} />}
          sx={{ height: 24, fontSize: '0.68rem', fontWeight: 600, borderColor: theme.palette.border.main, color: theme.palette.text.secondary, '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main } }}
        >
          Curate
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', px: { xs: 2, sm: 3, md: 4 }, pt: 10, pb: 3 }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 4 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <GymIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>AI Gym</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Monitor AI quality, manage golden set benchmarks, and curate past incidents
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small">
              <Select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                displayEmpty
                sx={{ height: 40, fontSize: '0.8rem', backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.primary, fontWeight: 500, minWidth: 180, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>Select client…</MenuItem>
                {clients.map((c) => <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Tooltip title="Run all benchmarks (async)">
              <span>
                <Button
                  disabled={!clientId || runAllLoading || entries.length === 0}
                  variant="outlined"
                  size="small"
                  startIcon={runAllLoading ? <CircularProgress size={13} color="inherit" /> : <RunAllIcon sx={{ fontSize: 16 }} />}
                  onClick={handleRunAll}
                  sx={{ height: 40, fontSize: '0.78rem', fontWeight: 600, borderColor: theme.palette.border.main, color: theme.palette.text.secondary, '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main } }}
                >
                  Run all
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Refresh">
              <span>
                <IconButton disabled={!clientId} onClick={() => clientId && loadAll(clientId)} sx={{ bgcolor: theme.palette.background.tertiary, color: theme.palette.info.main, borderRadius: 1, width: 40, height: 40, '&:hover': { bgcolor: theme.palette.info.main, color: theme.palette.background.tertiary, transform: 'rotate(180deg)' }, transition: 'all 0.4s ease' }}>
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {!clientId ? (
          <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 1.5, p: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>Select a client to manage the golden set</Typography>
          </Box>
        ) : (
          <>
            {/* Deployment banner */}
            {reportLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : report ? (
              <DeploymentBanner report={report} />
            ) : null}

            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${theme.palette.border.light}`, mb: 2.5 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ '& .MuiTab-root': { py: 0.75, minHeight: 36, fontSize: '0.82rem', fontWeight: 600 }, '& .MuiTabs-indicator': { height: 2 } }}
              >
                <Tab label="Inbox" icon={<NotificationsIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
                <Tab label="Coverage" icon={<AnalyticsIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
                <Tab label={`Golden Set (${entries.length})`} />
                <Tab label={`Candidates (${candidates.length})`} />
              </Tabs>
            </Box>

            {tab === 0 && clientId && (
              <NotificationsTab clientId={clientId} onViewTrace={setTraceTicketId} onGoToTab={setTab} />
            )}

            {tab === 1 && clientId && (
              <CoverageTab clientId={clientId} onViewTrace={setTraceTicketId} />
            )}

            {tab === 2 && (
              <DataTable<GoldenSetEntry>
                columns={entryCols}
                data={entries}
                rowKey={(row) => row.id}
                isLoading={entriesLoading}
                emptyMessage="No golden set entries yet"
                emptySubMessage="Switch to the Candidates tab to find exemplary sessions to curate"
                minTableWidth={600}
                rowHeight={52}
              />
            )}

            {tab === 3 && (
              <DataTable<GoldenSetCandidate>
                columns={candidateCols}
                data={candidates}
                rowKey={(row) => row.session_id}
                isLoading={candidatesLoading}
                emptyMessage="No candidates found"
                emptySubMessage="Candidates require outcome=handled, ≥3 completed steps, an AI summary, and ≥75% acceptance rate"
                minTableWidth={600}
                rowHeight={52}
              />
            )}
          </>
        )}

        {/* Toast */}
        {toastMsg && (
          <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.border.main}`, color: theme.palette.text.primary, px: 2.5, py: 1.25, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{toastMsg}</Typography>
          </Box>
        )}

        <CurateDialog candidate={curateTarget} clientId={clientId} onClose={() => setCurateTarget(null)} onCurated={handleCurated} />
        <RunValidationDialog entry={resultsTarget} onClose={() => setResultsTarget(null)} />
        <PipelineTraceModal open={!!traceTicketId} ticketId={traceTicketId} onClose={() => setTraceTicketId(null)} />
      </Box>
    </Box>
  );
};

export default AIGymPage;
